import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import { FaBell, FaTrash } from "react-icons/fa6";

import {
  GetNotificationCount,
  GetNotificationList,
  UpdateNotificationStatus,
} from "../../services/Notification/NotificationApi";

const getMessagePreview = (message = "", maxLength = 72) => {
  const text = String(message || "").trim();
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}...` : text;
};

const getCountValue = (response) => {
  const directNotificationCount = Number(
    response?.responseData?.notificationCount ??
      response?.responseData?.NotificationCount ??
      response?.notificationCount ??
      response?.NotificationCount,
  );

  if (Number.isFinite(directNotificationCount)) {
    return directNotificationCount;
  }

  const countKeys = [
    "notificationCount",
    "NotificationCount",
    "unreadCount",
    "UnreadCount",
    "count",
    "Count",
    "totalCount",
    "TotalCount",
    "total",
    "Total",
  ];

  const readCount = (value) => {
    if (value === null || value === undefined) return null;

    if (typeof value === "number" || typeof value === "string") {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const count = readCount(item);
        if (count !== null) return count;
      }

      return null;
    }

    if (typeof value === "object") {
      for (const key of countKeys) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const count = readCount(value[key]);
          if (count !== null) return count;
        }
      }

      for (const nestedValue of Object.values(value)) {
        const count = readCount(nestedValue);
        if (count !== null) return count;
      }
    }

    return null;
  };

  return readCount(response) || 0;
};

const NOTIFICATION_REFRESH_MS = 120000;

const NotificationMenu = () => {
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const userKeyID = useMemo(() => {
    try {
      const login = JSON.parse(localStorage.getItem("login")) || {};
      return login.userKeyID || login.userKeyId || login.UserKeyID || null;
    } catch {
      return null;
    }
  }, []);

  const loadCount = useCallback(async () => {
    if (!userKeyID) return;

    try {
      const response = await GetNotificationCount(userKeyID);
      if (!response?.response?.status) {
        setCount(getCountValue(response));
      }
    } catch (error) {
      console.error("Error loading notification count:", error);
    }
  }, [userKeyID]);

  const loadNotifications = useCallback(async ({ showLoader = true } = {}) => {
    if (!userKeyID) return;

    if (showLoader) setIsLoading(true);
    try {
      const response = await GetNotificationList({
        pageNo: 0,
        pageSize: 30,
        userKeyID,
      });

      if (response?.statusCode === 200) {
        const nextRows = response?.responseData?.data || [];
        setRows(nextRows);
        setCount(nextRows.filter((item) => !item.isSeen).length);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
      setRows([]);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [userKeyID]);

  useEffect(() => {
    loadCount();
    const refreshTimer = setInterval(() => {
      loadCount();
      if (isOpen) {
        loadNotifications({ showLoader: false });
      }
    }, NOTIFICATION_REFRESH_MS);

    return () => clearInterval(refreshTimer);
  }, [isOpen, loadCount, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((current) => {
      const nextOpen = !current;
      if (nextOpen) loadNotifications({ showLoader: true });
      return nextOpen;
    });
  };

  const handleOpenNotification = async (notification) => {
    setSelectedId((current) =>
      current === notification.id ? null : notification.id,
    );

    if (notification.isSeen || !notification.notificationKeyID || !userKeyID) {
      return;
    }

    try {
      setUpdatingId(notification.id);
      const response = await UpdateNotificationStatus({
        userKeyID,
        notificationKeyID: notification.notificationKeyID,
        notificationStatus: "Seen",
      });

      if (response?.statusCode === 200) {
        setRows((currentRows) =>
          currentRows.map((row) =>
            row.notificationKeyID === notification.notificationKeyID
              ? { ...row, isSeen: true, notificationStatus: "Seen" }
              : row,
          ),
        );
        setCount((currentCount) => Math.max(currentCount - 1, 0));
      }
    } catch (error) {
      console.error("Error marking notification as seen:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (event, notification) => {
    event.stopPropagation();

    if (!notification.notificationKeyID || !userKeyID) return;

    try {
      setUpdatingId(notification.id);
      const response = await UpdateNotificationStatus({
        userKeyID,
        notificationKeyID: notification.notificationKeyID,
        notificationStatus: "Delete",
      });

      if (response?.statusCode === 200) {
        setRows((currentRows) =>
          currentRows.filter(
            (row) => row.notificationKeyID !== notification.notificationKeyID,
          ),
        );
        if (!notification.isSeen) {
          setCount((currentCount) => Math.max(currentCount - 1, 0));
        }
        setSelectedId((current) =>
          current === notification.id ? null : current,
        );
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div ref={menuRef} className="relative">
      <Tooltip title="Notifications">
        <button
          type="button"
          onClick={handleToggle}
          className="notification-trigger relative flex h-10 w-10 items-center justify-center rounded-2xl border border-border bg-white text-text-primary shadow-sm transition hover:-translate-y-[1px] hover:border-primary hover:text-primary"
        >
          <FaBell className="text-base" />
          {count > 0 ? (
            <span className="notification-count-badge absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1.5 text-[10px] font-extrabold leading-none text-white shadow-[0_4px_10px_rgba(220,38,38,0.35)]">
              {count > 99 ? "99+" : count}
            </span>
          ) : null}
        </button>
      </Tooltip>

      {isOpen ? (
        <div className="notification-popover absolute right-0 z-40 mt-3 w-[520px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-border bg-white shadow-xl">
          <div className="notification-popover__header flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h3 className="text-sm font-bold text-text-primary">Notifications</h3>
              <p className="mt-0.5 text-xs text-text-secondary">
                {count} unread notification{count === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="notification-popover__body max-h-[620px] overflow-y-auto p-3">
            {isLoading ? (
              <div className="py-8 text-center text-sm text-text-secondary">
                Loading notifications...
              </div>
            ) : rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-text-secondary">
                No notifications found.
              </div>
            ) : (
              rows.map((notification) => {
                const isSelected = selectedId === notification.id;
                const isUnread = !notification.isSeen;

                return (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpenNotification(notification)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleOpenNotification(notification);
                      }
                    }}
                    className={`notification-item mb-2 block w-full rounded-2xl border px-4 py-3 text-left transition ${
                      isUnread
                        ? "notification-item--unread border-primary/20 bg-primary/10 hover:bg-primary/15"
                        : "notification-item--seen border-slate-100 bg-white hover:bg-background-muted"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                          isUnread ? "bg-primary" : "bg-slate-300"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="truncate text-sm font-bold text-text-primary">
                            {notification.title || "Notification"}
                          </h4>
                          <span className="shrink-0 text-[11px] font-medium text-text-secondary">
                            {notification.createdOnDate || "-"}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-text-secondary">
                          {isSelected
                            ? notification.message || "-"
                            : getMessagePreview(notification.message)}
                        </p>
                        {isSelected ? (
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="text-xs text-text-secondary">
                              <span className="font-bold text-text-primary">
                                Reference Type:
                              </span>{" "}
                              {notification.referenceType || "-"}
                            </div>
                            <button
                              type="button"
                              disabled={updatingId === notification.id}
                              onClick={(event) => handleDelete(event, notification)}
                              className="notification-delete flex h-8 w-8 items-center justify-center rounded-xl border border-rose-100 bg-rose-50 text-rose-600 transition hover:border-rose-200 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationMenu;
